import { FormItem } from '../../../store/refinance/RefinanceContext';
import { RefinanceRequest } from '../../../models/RefinanceRequest';
import { convertDateObject, convertToFirstOfTheNextMonth } from '../../../utils/dates';
import { RemainingMortgageRequest } from '../../../models/RemainingMortgageRequest';
import { percentageToFloat } from '../../../utils/formatting';

export const formItems = {
	general: {
		remainingTotalMortgageAmount: 'remainingTotalMortgageAmount',
		interestBase: 'interestBase',
	},
	step1: {
		housePrice: 'housePrice',
		countMortgageParts: 'countMortgageParts',
		tooltips: {
			countMortgageParts: 'Een hypotheek kan uit meerdere delen bestaan, bijvoorbeeld een annuïteitenhypotheek met een aflossingsvrij deel. Elk deel kan een andere rente, rentevastperiode en/of looptijd hebben. Geef hier aan uit hoeveel delen jouw hypotheek bestaat.'
		}
	},
	step2: {
		mortgageParts: 'mortgageParts',
		mortgagePart: {
			mortgageType: 'mortgageType',
			mortgageTermYears: 'mortgageTermYears',
			mortgageTermStart: 'mortgageTermStart',
			currentInterestRate: 'currentInterestRate',
			interestRateFixedFor: 'interestRateFixedFor',
			interestRateFixedUntil: 'interestRateFixedUntil',
			mortgageAmountStartFixedInterestRate: 'mortgageAmountStartFixedInterestRate',
			//mortgageAmountRemaining: 'mortgageAmountRemaining',
			isThePenaltyAmountKnown: 'isThePenaltyAmountKnown',
			penaltyAmount: 'penaltyAmount',
			penaltyFreePayOffPercentage: 'penaltyFreePayOffPercentage',
			comparativeInterest: 'comparativeInterest',
			newInterest: 'newInterest',
		},
		toolTips: {
			mortgageTerm: 'Wat is de looptijd (de periode waarin je de hypotheek moet afbetalen) van de hypotheek en op welke datum is de hypotheek afgesloten?',
			currentInterestRate: 'Wat is de hypotheekrente die je nu betaalt voor dit hypotheekdeel?',
			interestRateFixed: '<p>Voor hoe lang is de huidige hypotheekrente vastgezet en wanneer eindigt deze rentevastperiode? De resterende rentevaste periode heeft invloed op de hoogte van de oversluitkosten. Lees <a href="https://www.hypotheker.nl/begrippenlijst/hypotheek-afsluiten/boeterente/" target="_blank" rel="nofollow">hier</a> meer.</p>',
			mortgageAmountStartFixedInterestRate: 'Wat was de hoogte van dit hypotheekdeel bij de start van de huidige rentevastperiode?',
			isThePenaltyAmountKnown: '<p>Als je eerder dan afgesproken je hypotheek aflost dan moet je misschien een vergoeding betalen voor vervroegd aflossen. Dit wordt ook wel boeterente genoemd. Deze vergoeding vraag je op bij je huidige hypotheekverstrekker. <a href="https://www.hypotheker.nl/begrippenlijst/hypotheek-afsluiten/boeterente/" target="_blank" rel="nofollow">Lees meer</a> over hoe de boeterente wordt berekend.<br>&nbsp;<br>Kies \'ja\' als je de te betalen vergoeding (boeterente) weet. Je kunt deze dan hieronder invullen.<br>Kies \'nee\' als je de vergoeding niet weet. Vul dan hieronder extra gegevens in zodat er een indicatie van de vergoeding berekend kan worden volgens de rekenregels van de AFM.<br>NB De berekening gaat er vanuit dat er sindsdien geen extra aflossingen zijn gedaan.</p>',
			penaltyAmount: 'Bij steeds meer hypotheekverstrekkers kun je de vergoeding (boeterente) zelf online berekenen. Anders kun je deze bij hen opvragen. Lees <a href="https://www.hypotheker.nl/begrippenlijst/hypotheek-afsluiten/boeterente/" target="_blank" rel="nofollow">hier</a> meer over boeterente.',
			penaltyFreePayOffPercentage: 'Elk jaar mag je een bepaald bedrag zonder kosten extra aflossen. Hoeveel dit is staat in je hypotheekcontract. Vaak is dit 10%.',
			comparativeInterest: '<p>De vergelijkingsrente is de rente die je hypotheekverstrekker moet gebruiken voor het berekenen van de boeterente. Je kunt de rente als volgt vinden:</p><ol><li>Ga naar de <a href="https://www.hypotheker.nl/rentestanden/" target="_blank" rel="nofollow">pagina met actuele hypotheekrentes</a>.</li><li>Kies bij \'Hypotheekvorm\' voor de vorm van je huidige hypotheek.</li><li>Kies bij \'Rentevaste periode\' voor de periode die groter of gelijk is aan jouw resterende rentevaste periode.</li><li>Kies bij \'Rentetarief\' voor NHG als je een NHG-hypotheek hebt. Heb je geen NHG-hypotheek, kies dan voor het percentage dat het beste de verhouding tussen de hoogte van je oorspronkelijke hypotheek en de waarde van je woning (op het moment van afsluiten van de hypotheek) weergeeft.</li><li>Kies bij \'Type bouw\' voor Bestaande bouw.</li><li>Zoek in de tabel jouw hypotheekverstrekker en soort hypotheek op.</li><li>Dit is de rente die je kunt gebruiken als vergelijkingsrente.</li></ol><p>Let op: heb je bij het afsluiten een korting of een opslag gekregen op de hypotheekrente? Dan mag je deze korting ook toepassen op de vergelijkingsrente. Individuele (onderhandelde) kortingen mogen niet worden toegepast.</p>'
		}
	},
	step3: {
		costs: {
			administration: 'administration',
			taxation: 'taxation',
			notary: 'notary',
		},
		mortgageWithNhg: 'mortgageWithNhg',
		toolTips: {
			administration: 'De advies- en bemiddelingskosten betaal je aan je financieel adviseur voor het oversluiten van de hypotheek. De kosten hiervoor liggen meestal tussen de € 2.500 en € 3.500.',
			taxation: 'De taxatiekosten liggen vaak tussen de 0,1 en 0,2 procent van de marktwaarde van de woning.',
			notary: 'De gemiddelde kosten voor de notaris zijn € 700. De werkelijke kosten kunnen afwijken, vraag daarom altijd een offerte aan bij verschillende notariskantoren.',
			nhg: 'De <a href="https://www.hypotheker.nl/begrippenlijst/huis-kopen/nationale-hypotheek-garantie/" target="_blank" rel="nofollow">Nationale Hypotheek Garantie (NHG)</a> is een garantie op hypotheken tot 450.000 euro. Sluit je een NHG-hypotheek af, dan leen je verantwoord en betaalbaar. Met een NHG-hypotheek loop je als woningeigenaar minder risico en profiteer je van een <a href="https://www.hypotheker.nl/begrippenlijst/hypotheek-afsluiten/rentekorting-hypotheek/" target="_blank" rel="nofollow">rentekorting</a>.'
		}
	},
	step4: {
		interest: 'interest',
		lowestInterest: 'lowestInterest',
		customInterest: 'customInterest',
		toolTips: {
			preferredInterestAndPeriod: '<p>Kies de rentevastperiode en bijbehorende rente voor de {mortgageType} van {mortgageAmountStartFixedInterestRate}, afgesloten op {mortgageTermStart}.<br>De resterende lening is nu {residualDebt}, deze loopt nog {durationTermString} tot {endDate}.</p>'
		}
	}
};

export const getPostData = (data: FormItem): RefinanceRequest => {
	//console.log(data)
	let postData: RefinanceRequest = {
		// transferDate: "2022-05-25T09:23:42.263Z",
		transferCost: 0,
		cost: {
			advice: data[formItems.step3.costs.administration],
			taxation: data[formItems.step3.costs.taxation],
			notary: data[formItems.step3.costs.notary],
			},
		withNhg: data[formItems.step3.mortgageWithNhg],
		mortgagePart: []
	}

	//console.log(data[formItems.step2.mortgageParts])
	data[formItems.step2.mortgageParts].forEach((mortgagePart: any, index: number) => {

		if (data[formItems.step1.countMortgageParts] >= (index + 1)) {
			let part = {
				form: parseInt(mortgagePart[formItems.step2.mortgagePart.mortgageType]),
				term: parseInt(mortgagePart[formItems.step2.mortgagePart.mortgageTermYears]) * 12,
				closed: convertDateObject(convertToFirstOfTheNextMonth(mortgagePart[formItems.step2.mortgagePart.mortgageTermStart])),
				mortgageInterest: percentageToFloat(mortgagePart[formItems.step2.mortgagePart.currentInterestRate]),
				durationRvp: parseInt(mortgagePart[formItems.step2.mortgagePart.interestRateFixedFor]),
				endRvp: convertDateObject(convertToFirstOfTheNextMonth(mortgagePart[formItems.step2.mortgagePart.interestRateFixedUntil])),
				loanBeginRvp: parseInt(mortgagePart[formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate]),
				penaltyInterestKnown: mortgagePart[formItems.step2.mortgagePart.isThePenaltyAmountKnown] === "true",
				penaltyInterestAmount: (mortgagePart[formItems.step2.mortgagePart.isThePenaltyAmountKnown] === "true") ? parseInt(mortgagePart[formItems.step2.mortgagePart.penaltyAmount]) : 0,
				penaltyFreePercentage: (mortgagePart[formItems.step2.mortgagePart.isThePenaltyAmountKnown] === "false") ? percentageToFloat(mortgagePart[formItems.step2.mortgagePart.penaltyFreePayOffPercentage]) : 0,
				comparativeInterest: (mortgagePart[formItems.step2.mortgagePart.isThePenaltyAmountKnown] === "false") ? percentageToFloat(mortgagePart[formItems.step2.mortgagePart.comparativeInterest]) : 0,
				newInterest: (data[formItems.step4.interest][index][formItems.step4.customInterest]) ? percentageToFloat(data[formItems.step4.interest][index][formItems.step4.customInterest]) : data[formItems.step4.interest][index][formItems.step4.lowestInterest]
			};

			postData.mortgagePart.push(part);
		}
	})


	return postData;
};

export function getPostDataRemainingMortgage(form: FormItem): RemainingMortgageRequest  {
	//console.log(form);
	let postData: RemainingMortgageRequest = {
		referenceDate: convertDateObject(new Date()),
		marketValue: parseInt(form[formItems.step1.housePrice]),
		mortgagePart: []
	}

	form[formItems.step2.mortgageParts].forEach((mortgagePart: any, index: number) => {
		//console.log(index < form[formItems.step1.countMortgageParts], form[formItems.step1.countMortgageParts], index);
		//if (index < form[formItems.step1.countMortgageParts] ) {
			let part = {
				form: parseInt(mortgagePart[formItems.step2.mortgagePart.mortgageType]),
				term: parseInt(mortgagePart[formItems.step2.mortgagePart.mortgageTermYears]) * 12,
				closed: convertDateObject(convertToFirstOfTheNextMonth(mortgagePart[formItems.step2.mortgagePart.mortgageTermStart])),
				mortgageInterest: percentageToFloat(mortgagePart[formItems.step2.mortgagePart.currentInterestRate]),
				durationRvp: parseInt(mortgagePart[formItems.step2.mortgagePart.interestRateFixedFor]),
				loanBeginRvp: parseInt(mortgagePart[formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate]),
				endRvp: convertDateObject(convertToFirstOfTheNextMonth(mortgagePart[formItems.step2.mortgagePart.interestRateFixedUntil]))
			};

			postData.mortgagePart.push(part);
		//}
	});

	return postData;
}

export const getDebugData = () => {
	return {
		[formItems.step1.housePrice] : 250000,
		[formItems.step1.countMortgageParts] : 2,
		[formItems.step2.mortgageParts]: [
			{
				[formItems.step2.mortgagePart.mortgageType]: 9, // 4 - lin, 9 - ann, 6 - afl
				[formItems.step2.mortgagePart.mortgageTermYears]: 30,
				[formItems.step2.mortgagePart.mortgageTermStart]: '2016-04-01',
				[formItems.step2.mortgagePart.currentInterestRate]: '2,1',
				[formItems.step2.mortgagePart.interestRateFixedFor]: 10,
				[formItems.step2.mortgagePart.interestRateFixedUntil]: '2026-04-01',
				[formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate]: 126000,
				//[formItems.step2.mortgagePart.mortgageAmountRemaining]: 280000,
				[formItems.step2.mortgagePart.isThePenaltyAmountKnown]: "true",
				[formItems.step2.mortgagePart.penaltyAmount]: 10000,
				[formItems.step2.mortgagePart.newInterest]: '1,85', // var, 5, 10
				[formItems.step2.mortgagePart.penaltyFreePayOffPercentage]: '10',
				[formItems.step2.mortgagePart.comparativeInterest]: '1,8',
			},
			{
				[formItems.step2.mortgagePart.mortgageType]: 9, // 4 - lin, 9 - ann, 6 - afl
				[formItems.step2.mortgagePart.mortgageTermYears]: 30,
				[formItems.step2.mortgagePart.mortgageTermStart]: '01-04-2016',
				[formItems.step2.mortgagePart.currentInterestRate]: '2,1',
				[formItems.step2.mortgagePart.interestRateFixedFor]: 10,
				[formItems.step2.mortgagePart.interestRateFixedUntil]: '1-04-2026',
				[formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate]: 126000,
				//[formItems.step2.mortgagePart.mortgageAmountRemaining]: 280000,
				[formItems.step2.mortgagePart.isThePenaltyAmountKnown]: "true",
				[formItems.step2.mortgagePart.penaltyAmount]: 100000,
				[formItems.step2.mortgagePart.newInterest]: '1,85', // var, 5, 10
				[formItems.step2.mortgagePart.penaltyFreePayOffPercentage]: '10',
				[formItems.step2.mortgagePart.comparativeInterest]: '1,8',
			},
			/*{
				[formItems.step2.mortgagePart.mortgageType]: 9, // lin, ann, afl
				[formItems.step2.mortgagePart.mortgageTermYears]: 30,
				[formItems.step2.mortgagePart.mortgageTermStart]: '01-05-2005',
				[formItems.step2.mortgagePart.currentInterestRate]: 2.7,
				[formItems.step2.mortgagePart.interestRateFixedFor]: 10,
				[formItems.step2.mortgagePart.interestRateFixedUntil]: '01-05-2028',
				[formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate]: 50000,
				//[formItems.step2.mortgagePart.mortgageAmountRemaining]: 30000,
				[formItems.step2.mortgagePart.isThePenaltyAmountKnown]: false,
				[formItems.step2.mortgagePart.penaltyFreePayOffPercentage]: 15,
				[formItems.step2.mortgagePart.comparativeInterest]: 3.4,
				[formItems.step2.mortgagePart.newInterest]: 2.4, // var 5, 10, 15
			},*/
		],
		[formItems.step3.costs.administration]: 2990,
		[formItems.step3.costs.taxation]: 495,
		[formItems.step3.costs.notary]: 700,
		[formItems.step3.mortgageWithNhg]: false
	}
}
